import { readCachedUser } from "../utils/cachedUser";
import { useState, useEffect, useRef } from "react";
import {
  collection, onSnapshot, orderBy,
  query, serverTimestamp, doc, setDoc, getDoc, writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";

const LINE_ID    = "@857fxeyd";

const css = `
  /* FAB */
  .fc-fab{position:fixed;bottom:24px;right:20px;z-index:9999;display:flex;flex-direction:column;align-items:flex-end;gap:10px;}
  .fc-main{width:54px;height:54px;border-radius:50%;background:#dc3728;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 18px rgba(247,71,53,0.5);transition:transform 0.2s,box-shadow 0.2s;flex-shrink:0;}
  .fc-main:hover{transform:scale(1.08);box-shadow:0 6px 24px rgba(247,71,53,0.6);}
  .fc-unread{position:absolute;top:-4px;right:-4px;width:18px;height:18px;background:#ff5555;border-radius:50%;font-size:0.65rem;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;pointer-events:none;}
  .fc-fab-inner{position:relative;}

  /* Panel */
  .fc-panel{position:fixed;bottom:88px;right:20px;z-index:9998;width:320px;max-height:480px;background:#ffffff;border:1px solid rgba(247,71,53,0.18);border-radius:18px;box-shadow:0 12px 40px rgba(0,0,0,0.5);display:flex;flex-direction:column;overflow:hidden;animation:fc-pop 0.22s ease;}
  @keyframes fc-pop{from{opacity:0;transform:translateY(12px) scale(0.96)}to{opacity:1;transform:none}}
  .fc-head{padding:14px 16px;background:#f4f5f7;border-bottom:1px solid rgba(247,71,53,0.1);display:flex;align-items:center;gap:10px;}
  .fc-head-av{width:34px;height:34px;border-radius:50%;background:#fff0ed;color:#b42318;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0;}
  .fc-head-info{flex:1;}
  .fc-head-name{font-family:'Prompt',sans-serif;font-size:0.88rem;font-weight:700;color:#242b36;}
  .fc-head-status{font-size:0.68rem;color:#dc3728;display:flex;align-items:center;gap:4px;}
  .fc-head-dot{width:6px;height:6px;border-radius:50%;background:#dc3728;}
  .fc-close{background:none;border:none;color:#88919e;cursor:pointer;font-size:1.1rem;padding:2px;line-height:1;}
  .fc-close:hover{color:#242b36;}

  /* Quick links */
  .fc-links{display:flex;gap:8px;padding:10px 12px;border-bottom:1px solid rgba(247,71,53,0.08);}
  .fc-link{flex:1;padding:7px 6px;border-radius:10px;border:1px solid rgba(247,71,53,0.15);background:transparent;color:#88919e;font-family:'Prompt',sans-serif;font-size:0.72rem;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:5px;text-decoration:none;transition:all 0.18s;}
  .fc-link:hover{border-color:rgba(247,71,53,0.4);color:#242b36;}
  .fc-link.line{border-color:#a6dfb9;color:#087f3c;}
  .fc-link.line:hover{background:rgba(6,199,85,0.08);}
  .fc-link.phone{border-color:rgba(0,176,255,0.3);color:#00b0ff;}
  .fc-link.phone:hover{background:rgba(0,176,255,0.08);}

  /* Messages */
  .fc-msgs{flex:1;overflow-y:auto;padding:12px;display:flex;flex-direction:column;gap:8px;min-height:160px;}
  .fc-msgs::-webkit-scrollbar{width:4px;}
  .fc-msgs::-webkit-scrollbar-thumb{background:rgba(247,71,53,0.15);border-radius:2px;}
  .fc-msg-row{display:flex;min-width:0;}
  .fc-msg-row.user{justify-content:flex-end;}
  .fc-msg{max-width:86%;padding:10px 12px;border-radius:12px;font-family:'Prompt',sans-serif;font-size:14px;line-height:1.6;overflow-wrap:anywhere;white-space:pre-wrap;}
  .fc-msg.user{background:#fff0ed;border:1px solid #ffd0c7;color:#242b36;border-bottom-right-radius:4px;}
  .fc-msg.admin{background:#f4f5f7;border:1px solid #e2e6ee;color:#242b36;border-bottom-left-radius:4px;}
  .fc-msg-time{font-size:11px;color:#667085;margin-top:4px;display:block;}
  .fc-welcome{align-self:center;font-size:0.72rem;color:#5a7264;padding:6px 12px;background:rgba(255,255,255,0.03);border-radius:100px;border:1px solid rgba(247,71,53,0.08);text-align:center;}

  /* Input */
  .fc-inputbar{padding:10px 12px;border-top:1px solid #e2e6ee;display:flex;gap:8px;align-items:flex-end;background:#ffffff;}
  .fc-textarea{flex:1;min-width:0;background:#f5f6f8;border:1px solid #cbd5e1;border-radius:10px;padding:10px 11px;color:#242b36;caret-color:#242b36;font-family:'Prompt',sans-serif;font-size:14px;resize:none;outline:none;max-height:80px;line-height:1.5;}
  .fc-textarea::placeholder{color:#667085;opacity:1;}
  .fc-textarea:focus{border-color:#dc3728;box-shadow:0 0 0 2px #ffe5df;}
  .fc-sendbtn{width:34px;height:34px;border-radius:9px;border:none;background:#dc3728;color:#f5f6f8;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:0.9rem;transition:background 0.18s;}
  .fc-sendbtn:hover{background:#e63626;}
  .fc-sendbtn:disabled{background:#e4e7ec;color:#667085;opacity:1;cursor:not-allowed;}

  @media(max-width:480px){
    .fc-panel{right:8px;left:8px;width:auto;bottom:78px;max-height:420px;}
    .fc-fab{bottom:14px;right:12px;}
    .fc-main{width:48px;height:48px;}
    .fc-links{flex-wrap:wrap;padding:8px 10px;gap:6px;}
    .fc-link{font-size:0.68rem;padding:6px 5px;}
    .fc-msgs{min-height:120px;padding:10px;}
    .fc-inputbar{padding:8px 10px;}
    .fc-textarea{font-size:16px;}
    .fc-head{padding:10px 14px;}
    .fc-head-name{font-size:0.82rem;}
    .fc-head-status{font-size:0.62rem;}
  }
  @media(max-width:360px){
    .fc-panel{right:4px;left:4px;}
    .fc-links{grid-template-columns:1fr 1fr;display:grid;}
    .fc-link.line{grid-column:1/-1;}
  }
`;

function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
}

export default function FloatingContact() {
  const [open, setOpen]   = useState(false);
  const [msgs, setMsgs]   = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const sendingRef = useRef(false);
  const [unread, setUnread]   = useState(0);
  const msgsRef  = useRef(null);
  const openRef  = useRef(false); // ref ให้ snapshot callback อ่านค่า open ล่าสุดได้เสมอ

  const user = readCachedUser();
  const uid  = user.uid;
  const name = user.name || user.displayName || user.email?.split("@")[0] || "ผู้ใช้";

  // sync openRef กับ open state
  useEffect(() => { openRef.current = open; }, [open]);

  // ── subscribe to messages (subscribe ครั้งเดียว ไม่ re-sub เมื่อ open เปลี่ยน) ─
  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, "supportChats", uid, "messages"),
      orderBy("createdAt", "asc")
    );
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMsgs(list);
      const unreadMsgs = list.filter(m => m.isAdmin && !m.readByUser);
      if (openRef.current) {
        // แชทเปิดอยู่ → badge = 0, mark read ทันที
        setUnread(0);
        if (unreadMsgs.length > 0) {
          const batch = writeBatch(db);
          unreadMsgs.forEach(m => {
            batch.update(doc(db, "supportChats", uid, "messages", m.id), { readByUser: true });
          });
          batch.commit().catch(() => {});
        }
      } else {
        setUnread(unreadMsgs.length);
      }
    }, () => setError("โหลดแชทช่วยเหลือไม่สำเร็จ กรุณาปิดแล้วเปิดหน้าใหม่"));
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── mark read ทันทีที่ panel เปิด (ใช้ msgsRef เพื่อได้ค่าล่าสุดเสมอ) ───────
  const msgsDataRef = useRef([]);
  useEffect(() => { msgsDataRef.current = msgs; }, [msgs]);

  useEffect(() => {
    if (!open || !uid) return;
    setUnread(0);
    const unreadMsgs = msgsDataRef.current.filter(m => m.isAdmin && !m.readByUser);
    if (unreadMsgs.length === 0) return;
    const batch = writeBatch(db);
    unreadMsgs.forEach(m => {
      batch.update(doc(db, "supportChats", uid, "messages", m.id), { readByUser: true });
    });
    batch.commit().catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── auto scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }, 40);
    return () => clearTimeout(timer);
  }, [msgs, open]);

  // ── ensure support chat doc exists ────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "supportChats", uid)).then(snap => {
      if (!snap.exists()) {
        return setDoc(doc(db, "supportChats", uid), {
          userId: uid, userName: name,
          userEmail: user.email || "",
          createdAt: serverTimestamp(), lastMsg: "", lastAt: serverTimestamp(),
        });
      }
    }).catch(() => setError("เชื่อมต่อทีมช่วยเหลือไม่สำเร็จ กรุณาลองอีกครั้ง"));
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const text = input.trim();
    if (!text || sendingRef.current || !uid) return;
    sendingRef.current = true;
    const draft = input;
    setSending(true);
    setError("");
    try {
      const batch = writeBatch(db);
      batch.set(doc(collection(db, "supportChats", uid, "messages")), {
        text, isAdmin: false,
        senderId: uid, senderName: name,
        readByUser: true, readByAdmin: false,
        createdAt: serverTimestamp(),
      });
      batch.set(doc(db, "supportChats", uid), {
        userId: uid, userName: name,
        userEmail: user.email || "",
        lastMsg: text, lastAt: serverTimestamp(),
        lastSender: "user",
      }, { merge: true });
      await batch.commit();
      setInput(current => current === draft ? "" : current);
    } catch {
      setError("ส่งข้อความไม่สำเร็จ ข้อความยังอยู่ กรุณาลองอีกครั้ง");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <>
      <style>{css}</style>

      {/* ── Chat Panel ── */}
      {open && (
        <div className="fc-panel">
          <div className="fc-head">
            <div className="fc-head-av"><Icon name="shield" size={18} /></div>
            <div className="fc-head-info">
              <div className="fc-head-name">Klang Support</div>
              <div className="fc-head-status"><div className="fc-head-dot"/>พร้อมให้บริการ</div>
            </div>
            <button className="fc-close" aria-label="ปิดแชทช่วยเหลือ" onClick={() => setOpen(false)}>✕</button>
          </div>

          {/* Quick links */}
          <div className="fc-links">
            <a className="fc-link line" href={`https://line.me/ti/p/~${LINE_ID}`} target="_blank" rel="noreferrer">
              <svg width="14" height="14" viewBox="0 0 48 48"><path d="M24 4C12.95 4 4 11.86 4 21.5c0 5.8 3.27 10.96 8.35 14.27-.37 1.4-1.35 5.08-1.55 5.86-.24.96.35 1.95 1.3 1.64.77-.25 6.13-4.07 8.64-5.75A23.7 23.7 0 0024 39c11.05 0 20-7.86 20-17.5S35.05 4 24 4z" fill="currentColor"/></svg>
              LINE
            </a>
          </div>

          {/* Messages */}
          <div className="fc-msgs" ref={msgsRef}>
            <div className="fc-welcome">สวัสดีครับ! มีอะไรให้ช่วยไหม?</div>
            {msgs.map(m => (
              <div key={m.id} className={`fc-msg-row ${m.isAdmin ? "admin" : "user"}`}>
                <div className={`fc-msg ${m.isAdmin ? "admin" : "user"}`}>
                  {m.isAdmin && <div style={{fontSize:11,color:"#667085",marginBottom:3}}>Admin</div>}
                  {m.text}
                  <span className="fc-msg-time">{formatTime(m.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          {error && <p role="alert" style={{ margin: "8px 12px", color: "#b42318", fontSize: 12 }}>{error}</p>}
          <div className="fc-inputbar">
            <textarea
              className="fc-textarea"
              aria-label="ข้อความถึงทีมช่วยเหลือ"
              rows={1}
              placeholder={uid ? "พิมพ์ข้อความ..." : "กรุณาเข้าสู่ระบบก่อน"}
              value={input}
              disabled={!uid}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }}}
            />
            <button className="fc-sendbtn" aria-label="ส่งข้อความถึงทีมช่วยเหลือ" onClick={send} disabled={!input.trim() || sending || !uid}><Icon name="send" size={16} /></button>
          </div>
        </div>
      )}

      {/* ── FAB ── */}
      <div className="fc-fab">
        <div className="fc-fab-inner">
          {unread > 0 && !open && <div className="fc-unread">{unread}</div>}
          <button className="fc-main" aria-label={open ? "ปิดแชทช่วยเหลือ" : "เปิดแชทช่วยเหลือ"} aria-expanded={open} onClick={() => setOpen(o => !o)}>
            {open ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#f5f6f8">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#f5f6f8">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
